/**
 * CIPAA Candidates Manager Component
 *
 * Component for managing election candidates.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { DataTable, type DataTableColumn } from '../../../components';
import {
  CipaaCandidate,
  CipaaToken,
  CipaaCandidateStatus,
  CANDIDATE_STATUS_LABELS,
  CANDIDATE_STATUS_COLORS,
  formatDate,
  maskCPF,
} from '../../../types/cipaa';
import { EmployeeStatus } from '../../../types/employee';
import { cipaaService } from '../../../services/cipaaService';
import { employeeService } from '../../../services/employeeService';

interface CipaaCandidatesManagerProps {
  electionId: number;
  companyId: number;
  canManage: boolean;
}

interface EmployeeOption {
  id: number;
  label: string;
  cpf: string;
  occupation?: string;
  department?: string;
}

const getStatusChip = (status: CipaaCandidateStatus) => (
  <Chip
    label={CANDIDATE_STATUS_LABELS[status]}
    color={CANDIDATE_STATUS_COLORS[status]}
    size="small"
  />
);

const candidateColumns: DataTableColumn<CipaaCandidate>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (candidate) => candidate.employee?.name || '-',
  },
  { id: 'cpf', label: 'CPF', render: (candidate) => maskCPF(candidate.employee?.cpf || '') },
  {
    id: 'occupation',
    label: 'Cargo',
    render: (candidate) => candidate.employee?.occupation?.name || '-',
  },
  {
    id: 'department',
    label: 'Setor',
    render: (candidate) => candidate.employee?.department?.name || '-',
  },
  {
    id: 'registrationDate',
    label: 'Inscrição',
    render: (candidate) => formatDate(candidate.registrationDate),
  },
  { id: 'status', label: 'Status', render: (candidate) => getStatusChip(candidate.status) },
];

const tokenColumns: DataTableColumn<CipaaToken>[] = [
  {
    id: 'token',
    label: 'Token',
    primary: true,
    render: (token) => (
      <Typography variant="body2" fontFamily="monospace">
        {token.token}
      </Typography>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (token) => (
      <Chip
        label={token.isUsed ? 'Usado' : 'Disponível'}
        color={token.isUsed ? 'default' : 'success'}
        size="small"
      />
    ),
  },
  {
    id: 'usedByCpf',
    label: 'Usado Por',
    render: (token) => (token.usedByCpf ? maskCPF(token.usedByCpf) : '-'),
  },
  {
    id: 'usedAt',
    label: 'Usado Em',
    render: (token) => (token.usedAt ? formatDate(token.usedAt) : '-'),
  },
  {
    id: 'expiresAt',
    label: 'Expira Em',
    render: (token) => (token.expiresAt ? formatDate(token.expiresAt) : 'Sem expiração'),
  },
];

export const CipaaCandidatesManager: React.FC<CipaaCandidatesManagerProps> = ({
  electionId,
  companyId,
  canManage,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [candidates, setCandidates] = useState<CipaaCandidate[]>([]);
  const [tokens, setTokens] = useState<CipaaToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add candidate dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(null);
  const [addingCandidate, setAddingCandidate] = useState(false);

  // Token generation
  const [generatingToken, setGeneratingToken] = useState(false);

  useEffect(() => {
    loadData();
  }, [electionId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [candidatesData, tokensData] = await Promise.all([
        cipaaService.getCandidates(electionId),
        cipaaService.getTokens(electionId),
      ]);
      setCandidates(candidatesData);
      setTokens(tokensData);
    } catch (err: any) {
      setError(err.message ?? 'Erro ao carregar dados');
      enqueueSnackbar('Erro ao carregar candidatos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await employeeService.getEmployees({
        filters: { companyId, status: EmployeeStatus.ATIVO },
        limit: 1000,
      });
      const existingEmployeeIds = candidates.map((c) => c.employeeId);
      const options: EmployeeOption[] = response.data
        .filter((e: any) => !existingEmployeeIds.includes(e.id))
        .map((e: any) => ({
          id: e.id,
          label: e.name,
          cpf: e.cpf,
          occupation: e.occupation?.name,
          department: e.department?.name,
        }));
      setEmployees(options);
    } catch (err) {
      console.error('Error loading employees:', err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleOpenAddDialog = () => {
    setAddDialogOpen(true);
    setSelectedEmployee(null);
    loadEmployees();
  };

  const handleAddCandidate = async () => {
    if (!selectedEmployee) return;

    setAddingCandidate(true);
    try {
      await cipaaService.addCandidate(electionId, selectedEmployee.id);
      enqueueSnackbar('Candidato adicionado com sucesso', { variant: 'success' });
      setAddDialogOpen(false);
      loadData();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao adicionar candidato', { variant: 'error' });
    } finally {
      setAddingCandidate(false);
    }
  };

  const handleApprove = async (candidateId: number) => {
    try {
      await cipaaService.approveCandidate(electionId, candidateId);
      enqueueSnackbar('Candidato aprovado', { variant: 'success' });
      loadData();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao aprovar candidato', { variant: 'error' });
    }
  };

  const handleReject = async (candidateId: number) => {
    try {
      await cipaaService.rejectCandidate(electionId, candidateId);
      enqueueSnackbar('Candidato rejeitado', { variant: 'warning' });
      loadData();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao rejeitar candidato', { variant: 'error' });
    }
  };

  const handleRemove = async (candidateId: number) => {
    if (!window.confirm('Tem certeza que deseja remover este candidato?')) return;

    try {
      await cipaaService.removeCandidate(electionId, candidateId);
      enqueueSnackbar('Candidato removido', { variant: 'success' });
      loadData();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao remover candidato', { variant: 'error' });
    }
  };

  const handleGenerateToken = async () => {
    setGeneratingToken(true);
    try {
      const token = await cipaaService.generateToken(electionId);
      enqueueSnackbar('Token gerado com sucesso', { variant: 'success' });
      setTokens((prev) => [...prev, token]);
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao gerar token', { variant: 'error' });
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleCopyToken = (token: string) => {
    navigator.clipboard.writeText(token);
    enqueueSnackbar('Token copiado para a área de transferência', { variant: 'info' });
  };

  const handleCopyTokenLink = (token: string) => {
    const link = `${window.location.origin}/candidatura/${token}`;
    navigator.clipboard.writeText(link);
    enqueueSnackbar('Link de candidatura copiado', { variant: 'success' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" gutterBottom>
          <strong>Cadastro Manual:</strong> Utilize o botão "Adicionar Candidato" para registrar funcionários diretamente.
        </Typography>
        <Typography variant="body2">
          <strong>Token Compartilhado:</strong> Gere um token e compartilhe com os funcionários para que se inscrevam de forma autônoma.
        </Typography>
      </Alert>

      {/* Candidates Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Candidatos</Typography>
          {canManage && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadData}
              >
                Atualizar
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
              >
                Adicionar Candidato
              </Button>
            </Box>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <DataTable
          columns={candidateColumns}
          rows={candidates}
          getRowId={(candidate) => candidate.id}
          emptyState={{
            title: 'Nenhum candidato registrado',
            description:
              'Os candidatos são os funcionários inscritos nesta eleição, adicionados aqui durante o período de candidatura.',
          }}
          actions={
            canManage
              ? [
                  {
                    icon: <ApproveIcon fontSize="small" />,
                    tooltip: 'Aprovar',
                    color: 'success',
                    onClick: (candidate) => handleApprove(candidate.id),
                    hidden: (candidate) => candidate.status !== CipaaCandidateStatus.PENDING,
                  },
                  {
                    icon: <RejectIcon fontSize="small" />,
                    tooltip: 'Rejeitar',
                    color: 'error',
                    onClick: (candidate) => handleReject(candidate.id),
                    hidden: (candidate) => candidate.status !== CipaaCandidateStatus.PENDING,
                  },
                  {
                    icon: <DeleteIcon fontSize="small" />,
                    tooltip: 'Remover',
                    color: 'error',
                    onClick: (candidate) => handleRemove(candidate.id),
                  },
                ]
              : undefined
          }
        />
      </Box>

      {/* Tokens Section */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6">Tokens de Candidatura</Typography>
            <Typography variant="body2" color="text.secondary">
              Tokens para inscrição automática de candidatos
            </Typography>
          </Box>
          {canManage && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleGenerateToken}
              disabled={generatingToken}
            >
              {generatingToken ? 'Gerando...' : 'Gerar Token'}
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <DataTable
          columns={tokenColumns}
          rows={tokens}
          getRowId={(token) => token.id}
          emptyState={{
            title: 'Nenhum token gerado',
            description:
              'Os tokens são as credenciais de voto desta eleição: cada um permite que um funcionário vote uma única vez.',
          }}
          actions={
            canManage
              ? [
                  {
                    icon: <CopyIcon fontSize="small" />,
                    tooltip: 'Copiar Token',
                    onClick: (token) => handleCopyToken(token.token),
                    hidden: (token) => token.isUsed,
                  },
                  {
                    icon: <LinkIcon fontSize="small" />,
                    tooltip: 'Copiar Link de Candidatura',
                    color: 'primary',
                    onClick: (token) => handleCopyTokenLink(token.token),
                    hidden: (token) => token.isUsed,
                  },
                ]
              : undefined
          }
        />
      </Box>

      {/* Add Candidate Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Candidato</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={employees}
              value={selectedEmployee}
              onChange={(_e, value) => setSelectedEmployee(value)}
              loading={loadingEmployees}
              getOptionLabel={(option) => `${option.label} - ${maskCPF(option.cpf)}`}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {maskCPF(option.cpf)} | {option.occupation ?? 'Sem cargo'} |{' '}
                      {option.department ?? 'Sem setor'}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione o Funcionário"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEmployees ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAddCandidate}
            disabled={!selectedEmployee || addingCandidate}
          >
            {addingCandidate ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CipaaCandidatesManager;
